import React, { useState, useCallback, useContext, useMemo, useEffect } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import _ from 'underscore';
import { Stack } from '@fluentui/react';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { PerformanceMarkerContext } from '@bingads-webui-campaign-react/performance-marker-provider';
import { useFormService } from '@bingads-webui-react/form-core';
import { useWizardService, matchStepState, STEP_STATES } from '@bingads-webui-react/wizard';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { FormErrorGroup, UnmappedFormErrors } from '@bingads-webui-campaign-react/form-error';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { ActuationGroup } from '@bingads-webui-campaign-react/actuator';
import { getODataConfig } from '../model/constants';
import { getDisplayAdStepBaseStyles } from './styles';
import { DisplayAdsContainerWithCopilot, AdPreview } from './index';

const StepHeader = ({
  title,
  description,
}) => {
  const i18n = useContext(I18nContext);

  return (
    <React.Fragment>
      <h2 className="step-title">
        {i18n.getString(title)}
      </h2>
      {description &&
        <p className="step-description">
          {i18n.getString(description)}
        </p>
      }
    </React.Fragment>
  );
};

StepHeader.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
};

export const DisplayAdsWithCopilot = ({
  data = {},
  onDataChange,
  error,
  isAdGridEditorMode,
  hideFooter,
}) => {
  const {
    errors = [],
  } = error || {};

  const { scope } = useContext(ScopeContext) || {};
  const { cid, aid } = scope.values;

  const activity = useContext(ActivityContext);

  const { state } = useWizardService();

  const classes = useDefaultStyles(getDisplayAdStepBaseStyles);

  const { campaign = {}, editingAdId } = state.context.wizardData;

  const { Id: campaignId } = campaign;

  const currentStates = state.toStrings()[1];

  const i18n = useContext(I18nContext);

  const serverContext = useContext(ServerContext);
  const { Permissions, DynamicPermissions } = serverContext || {};
  const permissions = useMemo(() => ({ dynamic: { ...DynamicPermissions }, ...Permissions }), [DynamicPermissions, Permissions]);

  const perfMarker = useContext(PerformanceMarkerContext);

  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const oDataConfig = useMemo(() => getODataConfig(serverContext), [serverContext]);

  const { state: { context: { saveFailed } } } = useFormService();


  const removeEmptyAds = newAdsData => newAdsData.filter(ad => !_.isEmpty(ad.data));

  const onAdsValidation = useCallback((results) => {
    const { data: newAdsData, ...restResults } = results;

    const newAds = removeEmptyAds(newAdsData);

    const dataPatch = {
      data: [
        ...newAds,
      ],
      ...restResults,
    };
    onDataChange({ data: dataPatch, isValid: dataPatch.isValid });
  }, [onDataChange]);

  useEffect(() => {
    if (activity && matchStepState(state, STEP_STATES.LOADING)) {
      activity.signal(`onDisplayAdsStepLoading - ${currentStates}`);
    }

    if (activity && matchStepState(state, STEP_STATES.SAVING)) {
      activity.signal(`onDisplayAdsStepSaving - ${currentStates}`);
    }
  }, [activity, currentStates, state]);

  return (
    <div className={classNames([classes.displayAdsStepRoot, 'display-ads-step'])}>
      <div className="basic-ads-section">
        <ActuationGroup actuationProps={{ isActive: saveFailed }}>
          <AttentionBeaconGroup>
            <FormErrorGroup formErrorProps={{ errors, showAllValidationErrors: saveFailed }}>
              <Stack>
                <div>
                  <StepHeader
                    title={editingAdId ? _TL_('Edit your display ads') : _TL_('Create your display ads')}
                    description={_TL_('Provide the details and images you want to include in your ad.')}
                  />
                </div>
                <div className="server-errors">
                  <UnmappedFormErrors />
                </div>
                <Stack horizontal tokens={{ childrenGap: 50 }}>
                  <div style={{ width: '500px' }}>
                    {
                      data && data.data ? (
                        <DisplayAdsContainerWithCopilot
                          aid={aid}
                          cid={cid}
                          campaignId={campaignId}
                          i18n={i18n}
                          perfMarker={perfMarker}
                          onAdsValidation={onAdsValidation}
                          currentImageIndex={currentImageIndex}
                          ads={data.data}
                          oDataConfig={oDataConfig}
                          permissions={permissions}
                          initialAdsCount={1}
                          isAdGridEditorMode={isAdGridEditorMode}
                          hideFooter={hideFooter}
                        />
                      ) : null
                    }
                  </div>
                  <div>
                    <AdPreview
                      ads={data}
                      i18n={i18n}
                      perfMarker={perfMarker}
                      onUpdateCurrentImageIndex={setCurrentImageIndex}
                    />
                  </div>
                </Stack>
              </Stack>
            </FormErrorGroup>
          </AttentionBeaconGroup>
        </ActuationGroup>
      </div>
    </div>

  );
};

DisplayAdsWithCopilot.propTypes = {
  data: PropTypes.shape({
    data: PropTypes.shape({}),
  }),
  onDataChange: PropTypes.func.isRequired,
  error: PropTypes.shape({
    errors: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  isAdGridEditorMode: PropTypes.bool,
  hideFooter: PropTypes.bool,
};

DisplayAdsWithCopilot.defaultProps = {
  data: {},
  error: {},
  isAdGridEditorMode: false,
  hideFooter: false,
};

